'use client';

/**
 * Futuristic / HUD / sci-fi hero — control-surface aesthetic built
 * for tech-leaning learning platforms (AI academy, cloud bootcamp,
 * security training, devops). Ported from ThemeMaker's
 * {@code blocks/hero/HeroSignalDeck}.
 *
 * <h3>Visual signature</h3>
 * <ul>
 *   <li>HUD status chip with mono {@code SIGNAL :: ACTIVE} label,
 *       pulsing status dot, blinking terminal cursor</li>
 *   <li>Display headline with optional gradient-fill accent line
 *       (brand-500 → brand-300 by default)</li>
 *   <li>Animated vertical scan line sweeping the section (~10% opacity)</li>
 *   <li>Faint dot grid backdrop, masked radially toward the brand bloom</li>
 *   <li>Brand-coloured radial bloom anchored above the right column</li>
 *   <li>Corner brackets in all four section corners</li>
 *   <li>"MISSION CONSOLE" panel — bordered terminal frame with mono
 *       header, indexed skill modules, thin brand-coloured progress
 *       bars, optional active-status pulse per row, terminal prompt</li>
 *   <li>Stats row beneath the CTAs — mono labels, gradient numerals</li>
 * </ul>
 *
 * <p>All animations are CSS-only, GPU-friendly (opacity + transform
 * + top-percentage), and fully suppressed at
 * {@code prefers-reduced-motion: reduce}.
 *
 * <h3>Token strategy</h3>
 * Block-namespaced {@code --hero-deck-*} vars are escape hatches;
 * every fallback chains into a platform token ({@code brand-*},
 * {@code surface-*}, {@code text-*}). Block inherits the page
 * surface by default rather than stamping its own panel.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { asList, tx } from '../_shared/coerce';
import { sectionInner } from '../_shared/styles';
import { Button } from '@akademi/ui';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

interface ConsoleItem {
  /** Module/skill label, e.g. "AI Engineering". */
  label?: Localized;
  /** Progress percentage 0-100. Clamped at render. */
  progress?: number;
  /** Show the pulsing active dot beside the label. */
  active?: boolean;
}

interface ConsoleData {
  title?: Localized;
  items?: ConsoleItem[];
  /** Optional terminal prompt at the bottom (e.g.
   *  {@code "deploy :: next module"}). */
  prompt?: Localized;
}

interface StatItem {
  value?: Localized;
  label?: Localized;
}

export interface HeroSignalDeckProps {
  eyebrow?: Localized;
  status?: Localized;
  title?: Localized;
  titleAccent?: Localized;
  subtitle?: Localized;
  primaryCtaLabel?: Localized;
  primaryCtaHref?: string;
  secondaryCtaLabel?: Localized;
  secondaryCtaHref?: string;
  stats?: StatItem[];
  console?: ConsoleData;
  showConsole?: boolean;
  /** 'full' (default) lets content span the section edge-to-edge;
   *  'theme' constrains it to the page's max-width container. */
  maxWidth?: 'full' | 'theme';
}

/** The `t` returned by {@code useTranslation} — typed structurally so this
 *  package needs no direct dependency on i18next's own types. */
type Translate = (key: string) => string;

/** Built per render so a language switch re-reads the catalogue. */
const buildFallbackConsole = (t: Translate): ConsoleData => ({
  title: t('hero.signalDeck.consoleTitle'),
  items: [
    { label: t('hero.signalDeck.console.aiEngineering'), progress: 68, active: true },
    { label: t('hero.signalDeck.console.cloudArchitecture'), progress: 12 },
    { label: t('hero.signalDeck.console.securityFoundations'), progress: 92, active: true },
  ],
  prompt: t('hero.signalDeck.consolePrompt'),
});

/** Built per render so a language switch re-reads the catalogue. */
const buildFallbackStats = (t: Translate): StatItem[] => [
  { value: '42K', label: t('hero.signalDeck.stat.courses') },
  { value: '320+', label: t('hero.signalDeck.stat.paths') },
  { value: '1.2M', label: t('hero.signalDeck.stat.learners') },
];

export function HeroSignalDeck({
  eyebrow,
  status,
  title,
  titleAccent,
  subtitle,
  primaryCtaLabel,
  primaryCtaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
  stats,
  console: consoleProp,
  showConsole = true,
  maxWidth = 'full',
}: HeroSignalDeckProps) {
  const { t } = useTranslation('site-builder-blocks');
  const fallbackConsole = buildFallbackConsole(t);
  const fallbackStats = buildFallbackStats(t);
  // If the editor passed NOTHING for title/titleAccent we use both
  // defaults so the block has a hero look out of the box. If they
  // passed only one, we respect their choice and leave the other
  // empty — they meant their headline to be just that line.
  const hasUserHeadline = title !== undefined || titleAccent !== undefined;
  const titleText = tx(title) || (hasUserHeadline ? '' : t('hero.signalDeck.title'));
  const accentText = tx(titleAccent) || (hasUserHeadline ? '' : t('hero.signalDeck.titleAccent'));

  const statsArr = (asList<StatItem>(stats).length ? asList<StatItem>(stats) : fallbackStats).slice(
    0,
    4,
  );

  const consoleData: ConsoleData =
    consoleProp && typeof consoleProp === 'object' ? consoleProp : fallbackConsole;
  const consoleItems = (
    asList<ConsoleItem>(consoleData.items).length
      ? asList<ConsoleItem>(consoleData.items)
      : (fallbackConsole.items as ConsoleItem[])
  ).slice(0, 4);

  return (
    <BlockRoot
      as="section"
      className="hero-deck"
      style={{
        position: 'relative',
        overflow: 'hidden',
        // Inherit page surface — block sits ON the page rather than
        // stamping its own panel. Tenants can override --hero-deck-bg.
        background: 'var(--hero-deck-bg, var(--color-surface-page, transparent))',
        color: 'var(--hero-deck-text, var(--color-text-primary, #0f1218))',
        padding: 'clamp(40px, 5vw, 72px) var(--space-6, 24px) clamp(36px, 4.5vw, 64px)',
        ...(maxWidth === 'theme'
          ? {
              maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
              width: '100%',
              margin: '0 auto',
            }
          : {}),
      }}
    >
      {/* ===== Ambient layers ===== */}

      {/* Layer 1: dot grid, masked to appear only around the bloom. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage:
            'radial-gradient(circle at center, ' +
            'var(--hero-deck-grid-color, var(--color-border-default, rgba(0,0,0,0.12))) 1px, ' +
            'transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage: 'radial-gradient(80% 90% at 70% 30%, #000 30%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(80% 90% at 70% 30%, #000 30%, transparent 75%)',
          opacity: 'var(--hero-deck-grid-opacity, 0.65)' as unknown as number,
        }}
      />

      {/* Layer 2: brand-coloured radial bloom (the "light source"). */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(60% 80% at 78% 28%, ' +
            'var(--hero-deck-bloom, var(--color-brand-200, rgba(59,91,219,0.22))), ' +
            'transparent 70%)',
          opacity: 'var(--hero-deck-bloom-opacity, 0.6)' as unknown as number,
        }}
      />

      {/* Layer 3: animated vertical scan line. */}
      <div
        aria-hidden
        className="hero-deck-scan"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: 90,
          background:
            'linear-gradient(180deg, transparent, ' +
            'var(--hero-deck-scan-color, var(--color-brand-500, #3b5bdb)) 50%, ' +
            'transparent)',
          opacity: 'var(--hero-deck-scan-opacity, 0.10)' as unknown as number,
          filter: 'blur(2px)',
          pointerEvents: 'none',
        }}
      />

      {/* Layer 4: HUD corner brackets. */}
      <HudCorners />

      {/* ===== Content ===== */}
      <div
        style={{
          ...sectionInner,
          position: 'relative',
          maxWidth: 'var(--content-width-xl, 1240px)',
        }}
      >
        <div
          className="hero-deck-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: showConsole
              ? 'minmax(0, 1.05fr) minmax(min(380px, 100%), 0.95fr)'
              : '1fr',
            gap: 'clamp(var(--space-8, 32px), 4vw, var(--space-12, 48px))',
            alignItems: 'start',
          }}
        >
          {/* ===== LEFT COLUMN ===== */}
          <div>
            {/* HUD chip */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-2, 8px)',
                padding: 'var(--space-1-5, 6px) var(--space-3, 12px)',
                borderRadius: 'var(--radius-sm, 4px)',
                background: 'var(--hero-deck-chip-bg, var(--color-brand-50, rgba(59,91,219,0.06)))',
                border:
                  '1px solid var(--hero-deck-chip-border, var(--color-border-subtle, rgba(0,0,0,0.10)))',
                fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
                fontSize: '11px',
                fontWeight: 'var(--font-weight-semibold, 600)',
                letterSpacing: 'var(--font-tracking-widest, 0.16em)',
                textTransform: 'uppercase',
                color: 'var(--hero-deck-kicker, var(--color-brand-700, #1d4ed8))',
              }}
            >
              <span
                aria-hidden
                className="hero-deck-pulse"
                style={{
                  width: 'var(--space-1-5, 6px)',
                  height: 'var(--space-1-5, 6px)',
                  borderRadius: 'var(--radius-pill, 999px)',
                  background: 'var(--hero-deck-accent, var(--color-brand-500, #3b5bdb))',
                  boxShadow: '0 0 10px var(--hero-deck-accent, var(--color-brand-500, #3b5bdb))',
                }}
              />
              <span>{tx(eyebrow) || t('hero.signalDeck.eyebrow')}</span>
              <span style={{ opacity: 0.4 }}>::</span>
              <span>{tx(status) || t('hero.signalDeck.status')}</span>
              <span aria-hidden className="hero-deck-cursor" />
            </div>

            {/* Headline */}
            <h1
              style={{
                margin: 'var(--space-3-5, 14px) 0 0',
                maxWidth: 'var(--content-width-lg, 720px)',
                fontSize: 'clamp(36px, 5.5vw, 72px)',
                lineHeight: 'var(--font-leading-tight, 1.05)',
                letterSpacing: 'var(--font-tracking-tight, -0.035em)',
                fontFamily: 'var(--font-family-display, Inter Tight, system-ui, sans-serif)',
                fontWeight: 'var(--hero-deck-title-weight, 800)' as unknown as number,
                color: 'var(--hero-deck-text, var(--color-text-primary, #0f1218))',
              }}
            >
              {titleText && <span style={{ display: 'block' }}>{titleText}</span>}
              {accentText && (
                <span
                  className="hero-deck-accent-text"
                  style={{
                    display: 'block',
                    marginTop: titleText ? 'var(--space-1, 4px)' : 0,
                    backgroundImage:
                      'linear-gradient(135deg, ' +
                      'var(--hero-deck-accent-1, var(--color-brand-500, #3b5bdb)), ' +
                      'var(--hero-deck-accent-2, var(--color-brand-300, #93c5fd)))',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {accentText}
                </span>
              )}
              {accentText && (
                <span
                  aria-hidden
                  style={{
                    display: 'block',
                    marginTop: 'var(--space-3-5, 14px)',
                    height: 2,
                    width: 64,
                    background:
                      'linear-gradient(90deg, ' +
                      'var(--hero-deck-accent-1, var(--color-brand-500, #3b5bdb)), ' +
                      'transparent)',
                  }}
                />
              )}
            </h1>

            {/* Subtitle */}
            <p
              style={{
                maxWidth: 'var(--content-width-md, 620px)',
                margin: 'var(--space-5, 20px) 0 0',
                color: 'var(--color-text-secondary, #5f6675)',
                fontSize: 'clamp(16px, 1.5vw, 19px)',
                lineHeight: 'var(--font-leading-relaxed, 1.55)',
              }}
            >
              {tx(subtitle) ||
                'Build the skills that ship: AI engineering, cloud platforms, security operations. Trajectory deployed to your timeline.'}
            </p>

            {/* CTA row */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--space-3, 12px)',
                marginTop: 'var(--space-7, 28px)',
              }}
            >
              <Button
                href={primaryCtaHref || '#'}
                className="hero-deck-cta-primary"
                style={{
                  height: 'auto',
                  minHeight: 50,
                  gap: 'var(--space-2-5, 10px)',
                  padding: '0 22px',
                  borderRadius: 'var(--radius-md, 6px)',
                  background: 'var(--hero-deck-cta-bg, var(--color-brand-500, #3b5bdb))',
                  color: 'var(--hero-deck-cta-text, var(--color-text-inverse, #fff))',
                  fontWeight: 'var(--font-weight-bold, 700)',
                  fontSize: 'var(--font-size-sm, 14px)',
                  fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
                  letterSpacing: 'var(--font-tracking-widest, 0.10em)',
                  textTransform: 'uppercase',
                  boxShadow: 'var(--shadow-md, 0 6px 18px rgba(0,0,0,0.10))',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    display: 'inline-block',
                    width: 6,
                    height: 6,
                    borderRadius: 'var(--radius-pill, 999px)',
                    background: 'currentColor',
                    opacity: 0.9,
                  }}
                />
                {tx(primaryCtaLabel) || t('hero.signalDeck.primaryCta')}
                <span aria-hidden style={{ marginLeft: 'var(--space-1, 4px)' }}>
                  →
                </span>
              </Button>

              <Button
                href={secondaryCtaHref || '#'}
                className="hero-deck-cta-secondary"
                variant="outline"
                style={{
                  height: 'auto',
                  minHeight: 50,
                  gap: 'var(--space-2, 8px)',
                  padding: '0 var(--space-3, 12px)',
                  borderRadius: 'var(--radius-md, 6px)',
                  borderColor: 'var(--color-border-strong, rgba(0,0,0,0.18))',
                  color: 'var(--color-text-primary, #0f1218)',
                  fontWeight: 'var(--font-weight-semibold, 600)',
                  fontSize: 'var(--font-size-sm, 14px)',
                  fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
                  letterSpacing: 'var(--font-tracking-widest, 0.10em)',
                  textTransform: 'uppercase',
                }}
              >
                <span style={{ opacity: 0.45 }}>[</span>
                {tx(secondaryCtaLabel) || t('hero.signalDeck.secondaryCta')}
                <span style={{ opacity: 0.45 }}>]</span>
              </Button>
            </div>

            {/* Stats row — gradient mono numerals + uppercase labels. */}
            {statsArr.length > 0 && (
              <div
                style={{
                  marginTop: 'var(--space-9, 36px)',
                  paddingTop: 'var(--space-6, 24px)',
                  borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
                  display: 'grid',
                  gridTemplateColumns: `repeat(${statsArr.length}, minmax(0, auto))`,
                  columnGap: 'var(--space-9, 36px)',
                  rowGap: 'var(--space-4, 16px)',
                  alignItems: 'baseline',
                }}
              >
                {statsArr.map((s, i) => (
                  <div key={i}>
                    <div
                      style={{
                        fontFamily: 'var(--font-family-display, inherit)',
                        fontSize: 'clamp(22px, 2.4vw, 32px)',
                        fontWeight: 'var(--font-weight-bold, 700)',
                        letterSpacing: 'var(--font-tracking-snug, -0.02em)',
                        lineHeight: 'var(--font-leading-tight, 1.05)',
                        backgroundImage:
                          'linear-gradient(135deg, ' +
                          'var(--hero-deck-accent-1, var(--color-brand-500, #3b5bdb)), ' +
                          'var(--hero-deck-accent-2, var(--color-brand-300, #93c5fd)))',
                        WebkitBackgroundClip: 'text',
                        backgroundClip: 'text',
                        color: 'transparent',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {tx(s.value)}
                    </div>
                    <div
                      style={{
                        marginTop: 'var(--space-1-5, 6px)',
                        fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
                        fontSize: 'var(--font-size-xs, 12px)',
                        fontWeight: 'var(--font-weight-semibold, 600)',
                        letterSpacing: 'var(--font-tracking-widest, 0.18em)',
                        textTransform: 'uppercase',
                        color: 'var(--color-text-secondary, #5f6675)',
                      }}
                    >
                      {tx(s.label)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ===== RIGHT COLUMN — MISSION CONSOLE ===== */}
          {showConsole && <ConsolePanel data={consoleData} items={consoleItems} />}
        </div>
      </div>

      {/* ===== Component-scoped CSS ===== */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        /* Pulsing status dot. */
        .hero-deck-pulse {
          animation: heroDeckPulse 2.4s ease-in-out infinite;
        }
        @keyframes heroDeckPulse {
          0%, 100% { opacity: 1;   transform: scale(1); }
          50%      { opacity: 0.4; transform: scale(0.85); }
        }

        /* Blinking terminal cursor. steps(2) for hard on/off. */
        .hero-deck-cursor {
          display: inline-block;
          width: 7px;
          height: 11px;
          background: currentColor;
          margin-left: 4px;
          vertical-align: text-bottom;
          animation: heroDeckCursor 1s steps(2) infinite;
        }
        @keyframes heroDeckCursor {
          50% { opacity: 0; }
        }

        /* Vertical scan-line sweep — animating top% creates a "radar
           pass" across the section. 11s is slow enough not to pull
           attention from content. */
        .hero-deck-scan {
          animation: heroDeckScan 11s linear infinite;
        }
        @keyframes heroDeckScan {
          from { top: -10%; }
          to   { top: 110%; }
        }

        /* Brand-coloured focus ring so the a11y outline reads as part
           of the visual system. */
        .hero-deck a:focus-visible {
          outline: 2px solid var(--color-brand-500, #3b5bdb);
          outline-offset: 3px;
          border-radius: 4px;
        }

        /* CTA hover lift. Button already owns background/border hover
           states via its variant system; this layers a small translateY
           lift on top through the block-scoped className. */
        .hero-deck-cta-primary:hover,
        .hero-deck-cta-secondary:hover {
          transform: translateY(-2px);
        }

        /* Console row hover — subtle highlight, no transform (rows
           sit inside a panel; transforms would feel jittery). */
        .hero-deck-console-row {
          transition: background 160ms ease;
          margin: 0 calc(-1 * var(--space-2, 8px));
          padding: var(--space-2, 8px);
          border-radius: var(--radius-md, 6px);
        }
        .hero-deck-console-row:hover {
          background: var(--color-surface-muted, rgba(0,0,0,0.03));
        }

        /* Stack at < 920px — single column, console under the CTAs. */
        @media (max-width: 920px) {
          .hero-deck-grid {
            grid-template-columns: 1fr !important;
          }
        }

        /* Reduced-motion: kill every animation. Scan line hidden
           entirely (an immobile band looks like a layout bug); pulse
           and cursor stop on their final frame. */
        @media (prefers-reduced-motion: reduce) {
          .hero-deck-pulse,
          .hero-deck-cursor,
          .hero-deck-scan {
            animation: none !important;
          }
          .hero-deck-scan { display: none; }
          .hero-deck-cta-primary:hover,
          .hero-deck-cta-secondary:hover {
            transform: none;
          }
        }
      `,
        }}
      />
    </BlockRoot>
  );
}

// =============================================================================
// HUD corner brackets — four L-shapes in the section corners
// =============================================================================
//
// Pure CSS — each is a box with two adjacent borders. Absolutely
// positioned over the section, pointer-events: none so it can't
// intercept clicks.

function HudCorners() {
  const base: CSSProperties = {
    position: 'absolute',
    width: 18,
    height: 18,
    pointerEvents: 'none',
    color: 'var(--hero-deck-frame, var(--color-border-strong, rgba(0,0,0,0.20)))',
    opacity: 0.6,
  };
  return (
    <>
      <div
        aria-hidden
        style={{
          ...base,
          top: 18,
          left: 18,
          borderTop: '1.5px solid currentColor',
          borderLeft: '1.5px solid currentColor',
        }}
      />
      <div
        aria-hidden
        style={{
          ...base,
          top: 18,
          right: 18,
          borderTop: '1.5px solid currentColor',
          borderRight: '1.5px solid currentColor',
        }}
      />
      <div
        aria-hidden
        style={{
          ...base,
          bottom: 18,
          left: 18,
          borderBottom: '1.5px solid currentColor',
          borderLeft: '1.5px solid currentColor',
        }}
      />
      <div
        aria-hidden
        style={{
          ...base,
          bottom: 18,
          right: 18,
          borderBottom: '1.5px solid currentColor',
          borderRight: '1.5px solid currentColor',
        }}
      />
    </>
  );
}

// =============================================================================
// Mission Console — right-column terminal panel
// =============================================================================
//
// Bordered card with a mono header bar (status dot + title + window
// dots), a body of indexed skill rows with thin progress bars, and an
// optional terminal-prompt footer.

function ConsolePanel({ data, items }: { data: ConsoleData; items: ConsoleItem[] }) {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <div
      style={{
        position: 'relative',
        background: 'var(--hero-deck-console-bg, var(--color-surface-card, #fff))',
        border:
          '1px solid var(--hero-deck-console-border, var(--color-border-default, rgba(0,0,0,0.10)))',
        borderRadius: 'var(--radius-lg, 12px)',
        boxShadow: 'var(--hero-deck-console-shadow, var(--shadow-md, 0 4px 14px rgba(0,0,0,0.06)))',
        overflow: 'hidden',
      }}
    >
      {/* Header bar — terminal title + window dots */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-3-5, 14px) var(--space-5, 20px)',
          borderBottom: '1px solid var(--color-border-subtle, rgba(0,0,0,0.06))',
          background:
            'var(--hero-deck-console-header-bg, var(--color-surface-muted, rgba(0,0,0,0.03)))',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2, 8px)',
            fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
            fontSize: 'var(--font-size-xs, 12px)',
            fontWeight: 'var(--font-weight-semibold, 600)',
            letterSpacing: 'var(--font-tracking-widest, 0.18em)',
            textTransform: 'uppercase',
            color: 'var(--color-text-secondary, #5f6675)',
          }}
        >
          <span
            aria-hidden
            style={{
              width: 'var(--space-2, 8px)',
              height: 'var(--space-2, 8px)',
              borderRadius: 'var(--radius-pill, 999px)',
              background: 'var(--hero-deck-accent, var(--color-brand-500, #3b5bdb))',
              boxShadow: '0 0 8px var(--hero-deck-accent, var(--color-brand-500, #3b5bdb))',
            }}
          />
          {tx(data.title) || t('hero.signalDeck.consoleTitle')}
        </div>
        {/* Three staggered-opacity dots — terminal/window decoration. */}
        <div style={{ display: 'inline-flex', gap: 5 }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              aria-hidden
              style={{
                width: 'var(--space-2-5, 10px)',
                height: 'var(--space-2-5, 10px)',
                borderRadius: 'var(--radius-pill, 999px)',
                background: 'var(--color-border-strong, rgba(0,0,0,0.22))',
                opacity: 0.7 - i * 0.18,
              }}
            />
          ))}
        </div>
      </div>

      {/* Body — indexed rows with progress bars */}
      <div style={{ padding: 'var(--space-5, 20px) var(--space-5, 20px) var(--space-1-5, 6px)' }}>
        {items.map((item, i) => {
          const idx = String(i + 1).padStart(2, '0');
          const pct = Math.max(0, Math.min(100, item.progress ?? 0));
          return (
            <div
              key={i}
              className="hero-deck-console-row"
              style={{ marginBottom: 'var(--space-3-5, 14px)' }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--space-3, 12px)',
                  marginBottom: 'var(--space-2, 8px)',
                }}
              >
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'baseline',
                    gap: 'var(--space-2-5, 10px)',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
                      fontSize: '11px',
                      fontWeight: 'var(--font-weight-semibold, 600)',
                      color: 'var(--color-text-secondary, #5f6675)',
                      opacity: 0.7,
                      flexShrink: 0,
                    }}
                  >
                    [{idx}]
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--font-size-sm, 14px)',
                      fontWeight: 'var(--font-weight-semibold, 600)',
                      color: 'var(--color-text-primary, #0f1218)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {tx(item.label)}
                  </span>
                  {item.active && (
                    <span
                      aria-hidden
                      className="hero-deck-pulse"
                      style={{
                        width: 'var(--space-2, 8px)',
                        height: 'var(--space-2, 8px)',
                        borderRadius: 'var(--radius-pill, 999px)',
                        background: 'var(--hero-deck-accent, var(--color-brand-500, #3b5bdb))',
                        boxShadow:
                          '0 0 8px var(--hero-deck-accent, var(--color-brand-500, #3b5bdb))',
                        flexShrink: 0,
                        alignSelf: 'center',
                      }}
                    />
                  )}
                </div>
                <span
                  style={{
                    fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
                    fontSize: 'var(--font-size-xs, 12px)',
                    fontWeight: 'var(--font-weight-semibold, 600)',
                    color: 'var(--color-text-secondary, #5f6675)',
                    flexShrink: 0,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {pct.toString().padStart(2, '0')}%
                </span>
              </div>
              {/* Progress track + brand-gradient fill. */}
              <div
                style={{
                  height: 4,
                  borderRadius: 'var(--radius-sm, 4px)',
                  background: 'var(--color-border-subtle, rgba(0,0,0,0.06))',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${pct}%`,
                    background:
                      'linear-gradient(90deg, ' +
                      'var(--hero-deck-accent-1, var(--color-brand-500, #3b5bdb)), ' +
                      'var(--hero-deck-accent-2, var(--color-brand-300, #93c5fd)))',
                    borderRadius: 'var(--radius-sm, 4px)',
                    transition: 'width 320ms ease',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Optional terminal-prompt footer */}
      {data.prompt && (
        <div
          style={{
            padding: 'var(--space-3-5, 14px) var(--space-5, 20px)',
            borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,0.06))',
            fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
            fontSize: 'var(--font-size-xs, 12px)',
            color: 'var(--color-text-secondary, #5f6675)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2, 8px)',
          }}
        >
          <span
            style={{
              color: 'var(--hero-deck-accent, var(--color-brand-500, #3b5bdb))',
              fontWeight: 'var(--font-weight-bold, 700)',
            }}
          >
            ›
          </span>
          {tx(data.prompt)}
          <span aria-hidden className="hero-deck-cursor" style={{ height: 11 }} />
        </div>
      )}
    </div>
  );
}
